import React from 'react';
import { AddOn, QuestionnaireTemplate } from '@/types';
import { Edit2, FileText, Trash2 } from 'lucide-react';
import { Pagination } from '@shared/components/common/Pagination';
import { useAddOnsPage, useAddOnMutations, type AddOnListParams } from '@features/admin/data';

interface AddOnsListProps {
  page: number;
  pageSize: number;
  search: string;
  canEdit: boolean;
  questionnaireOptions: QuestionnaireTemplate[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (addOn: AddOn) => void;
}

export const AddOnsList: React.FC<AddOnsListProps> = ({
  page,
  pageSize,
  search,
  canEdit,
  questionnaireOptions,
  onPageChange,
  onPageSizeChange,
  onEdit
}) => {
  const addOnMutations = useAddOnMutations();
  const params: AddOnListParams = { page, pageSize, q: search || undefined };
  const result = useAddOnsPage(params);
  const addOns = result.items || [];
  const totalCount = Number(result.totalCount || 0);

  return (
    <>
      <div className="space-y-4">
        {addOns.length === 0 ? (
          <div className="bg-white p-6 rounded-xl border border-slate-200 text-center text-slate-500 italic">
            No add-ons found.
          </div>
        ) : (
          addOns.map((addon) => {
            const linkedQ = questionnaireOptions.find(
              (q) => q.id === addon.questionnaireConfig?.templateId
            );
            return (
              <div
                key={addon.id}
                className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm flex justify-between items-center hover:shadow-md transition-all"
              >
                <div className="flex-1 pr-8">
                  <h4 className="font-bold text-slate-900 text-lg leading-tight">{addon.name}</h4>
                  <p className="text-sm text-slate-500 mt-1 max-w-2xl">{addon.description}</p>
                  {linkedQ && (
                    <div className="mt-3 bg-teal-50 border border-teal-100 p-2.5 rounded-xl flex items-start gap-3 w-fit">
                      <div className="bg-teal-600 p-1.5 rounded-lg text-white">
                        <FileText className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase font-black text-teal-700 tracking-widest mb-0.5">
                          Form Assigned
                        </div>
                        <div className="text-[11px] font-bold text-teal-900 leading-tight">
                          {linkedQ.name}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest">
                      Est. {addon.durationMinutes} MINS
                    </span>
                    <span className="bg-teal-50 text-teal-700 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest border border-teal-100">
                      {addon.compatibleServiceIds?.length || 0} COMPATIBLE SERVICES
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-3 min-w-fit">
                  <div className="flex items-center gap-4">
                    <span className="font-black text-slate-900 text-xl">£{addon.price}</span>
                    {canEdit && (
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => onEdit(addon)}
                          className="p-2 bg-slate-50 rounded-lg text-blue-600 hover:bg-blue-50 border border-slate-100 transition-all"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => addOnMutations.remove(addon.id)}
                          className="p-2 bg-slate-50 rounded-lg text-red-600 hover:bg-red-50 border border-slate-100 transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {addon.tierPrices && Object.values(addon.tierPrices).some((v) => v != null) && (
                    <div className="flex flex-wrap justify-end gap-2">
                      {addon.tierPrices.bronze != null && (
                        <span className="text-[9px] font-black uppercase bg-orange-50 text-orange-700 border border-orange-100 px-2 py-0.5 rounded-full">
                          B: £{addon.tierPrices.bronze}
                        </span>
                      )}
                      {addon.tierPrices.silver != null && (
                        <span className="text-[9px] font-black uppercase bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-full">
                          S: £{addon.tierPrices.silver}
                        </span>
                      )}
                      {addon.tierPrices.gold != null && (
                        <span className="text-[9px] font-black uppercase bg-yellow-50 text-yellow-700 border border-yellow-100 px-2 py-0.5 rounded-full">
                          G: £{addon.tierPrices.gold}
                        </span>
                      )}
                      {addon.tierPrices.platinum != null && (
                        <span className="text-[9px] font-black uppercase bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded-full">
                          P: £{addon.tierPrices.platinum}
                        </span>
                      )}
                      {addon.tierPrices.diamond != null && (
                        <span className="text-[9px] font-black uppercase bg-cyan-50 text-cyan-700 border border-cyan-100 px-2 py-0.5 rounded-full">
                          D: £{addon.tierPrices.diamond}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={[25, 50, 100]}
      />
    </>
  );
};
